import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetOrganisations } from 'features/organisations/hooks/useGetOrganisations';
import { LazyAutocompleteInput } from 'greener-ui-library';
import { Organisation } from 'types';

const createOrganisationFilter = (filterKey: string) => ({
  filterKey,
});

type OrganisationFilterProps = {
  updateFilters: (filters: Array<{ filterKey: string; value: string }>) => void;
  clearFilter: (filterKey: string) => void;
  getFilterValues: (filterKey: string) => string | string[] | undefined;
  filterKey?: string;
};

export const OrganisationFilter = ({
  updateFilters,
  clearFilter,
  getFilterValues,
  filterKey = 'project_organisation__name__eq',
}: OrganisationFilterProps) => {
  const { t } = useTranslation();
  const filteredValue = getFilterValues?.(filterKey);
  const [filteredOrgValue] = Array.isArray(filteredValue) ? filteredValue : filteredValue ? [filteredValue] : [''];

  const [search, setSearch] = useState('');
  const { organisations, isFetching, goToNextPage, hasNextPage } = useGetOrganisations({
    accumulateData: true,
    searchTerm: search,
    reactQueryConfig: { suspense: false },
  });

  const accumulatedData = (organisations as { accumulatedData?: Organisation[] })?.accumulatedData ?? [];
  const organisationFilter = createOrganisationFilter(filterKey);

  const handleSelectionChange = (org, reason) => {
    if (reason === 'clear' || reason === 'removeOption' || !org) {
      clearFilter(organisationFilter.filterKey);
      return;
    }

    updateFilters([
      {
        ...organisationFilter,
        value: org.name,
      },
    ]);
  };

  const selectedOrg =
    accumulatedData.find((org: Organisation) => org.name === filteredOrgValue) ??
    (filteredOrgValue ? ({ name: filteredOrgValue } as Organisation) : null);

  return (
    <LazyAutocompleteInput<Organisation>
      name="organization"
      data-testid="organization-select"
      label={t('entities:organisation.nameSingular')}
      placeholder={t('entities:organisation.selectOrganisation')}
      options={accumulatedData}
      loading={isFetching}
      onSearchChange={setSearch}
      onChange={(_, value, reason) => handleSelectionChange(value, reason)}
      value={selectedOrg}
      onLoadMore={goToNextPage}
      hasNextPage={hasNextPage}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option?.name)}
    />
  );
};
